import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  TouchableOpacity,
  Alert,
  Modal,
  TouchableWithoutFeedback,
  TextInput
} from "react-native";
import Glob from "src/globalConstants";
import NavBar from "src/components/navBar";
import { MaterialIcons } from "@expo/vector-icons";
import moment from "moment";
import Database from "src/backend/database";
import Analytics from "src/backend/analytics";
import Rex from "src/globalState";
import { Skeleton } from "native-base";
import ChildCard from "src/components/ChildCard";
import Util from "src/utility";
import Icon from "src/components/Icon";

const { width } = Glob.get("dimensions");

export default function CommerceSubscriptionDetails({ route, navigation }) {
  const { subscription } = route.params;
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [children, setChildren] = useState({});
  const [classrooms, setClassrooms] = useState({});
  const [isLoadingChild, setIsLoadingChild] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(subscription);
  const [showBillingMethodModal, setShowBillingMethodModal] = useState(false);
  const [isChangingBillingMethod, setIsChangingBillingMethod] = useState(false);
  const [daysUntilDue, setDaysUntilDue] = useState("30");

  // Get the first item's price and product info
  const item = currentSubscription.items.data[0];
  const product = item?.price?.product;
  const interval = item?.price?.recurring?.interval || "month";
  const intervalCount = item?.price?.recurring?.interval_count || 1;
  const quantity = item?.quantity || 1;
  const unitAmount = item?.price?.unit_amount / 100; // Unit price in dollars
  const totalAmount = unitAmount * quantity; // Total amount accounting for quantity

  // Check if subscription has a child attached
  const tcChildID = currentSubscription?.metadata?.tcChildID;

  // Check if customer has a default payment method
  const hasDefaultPaymentMethod = !!currentSubscription?.customer
    ?.invoice_settings?.default_payment_method;

  useEffect(() => {
    Analytics.logEvent("view_commerceSubscriptionDetails");
  }, []);

  useEffect(() => {
    if (tcChildID && Rex.getConfig()?.transparentClassroomIntegrationEnabled) {
      fetchChildData();
    }
  }, [tcChildID]);

  const fetchChildData = async () => {
    try {
      setIsLoadingChild(true);
      const response = await Database.tcCheckIntegrationStatus();
      const { status } = response || {};
      const connected = status === Glob.get("tcIntegrationStatuses").CONNECTED;

      if (connected) {
        const childrenResponse = await Database.tcFetchChildren();
        const childrenByID = {};
        (childrenResponse?.data || []).forEach((child) => {
          childrenByID[child.id] = child;
        });
        setChildren(childrenByID);

        // Fetch classrooms if needed
        if (childrenByID[tcChildID]?.classroom_ids?.length > 0) {
          const classroomsResponse = await Database.tcFetchClassrooms();
          const classroomsByID = {};
          (classroomsResponse?.data || []).forEach((classroom) => {
            classroomsByID[classroom.id] = classroom;
          });
          setClassrooms(classroomsByID);
        }
      }
    } catch (error) {
      console.error("Error fetching child data:", error);
    } finally {
      setIsLoadingChild(false);
    }
  };

  const handleChangeBillingMethod = () => {
    // Only allow opening the modal if we have a payment method or we're switching from auto-pay to invoice
    if (
      hasDefaultPaymentMethod ||
      currentSubscription.collection_method === "charge_automatically"
    ) {
      Analytics.logEvent(
        "touch_commerceSubscriptionDetails_changeBillingMethod"
      );
      setShowBillingMethodModal(true);
    } else {
      Alert.alert(
        "Payment Method Required",
        "The customer needs to have a default payment method set up before switching to automatic payments."
      );
    }
  };

  const renderBillingMethodButton = () => {
    let buttonText = "Change to Auto-Charge";
    if (currentSubscription.collection_method === "charge_automatically") {
      buttonText = "Change to Send Invoice";
    }

    if (isChangingBillingMethod) {
      buttonText = "Processing...";
    }

    return (
      <TouchableOpacity
        style={[
          styles.modalButton,
          {
            ...styles.modalConfirmButton,
            backgroundColor: Rex.getConfig()?.colors?.button,
            opacity:
              !hasDefaultPaymentMethod &&
              currentSubscription.collection_method !== "charge_automatically"
                ? 0.5
                : 1
          }
        ]}
        onPress={confirmChangeBillingMethod}
        disabled={
          isChangingBillingMethod ||
          (!hasDefaultPaymentMethod &&
            currentSubscription.collection_method !== "charge_automatically")
        }
      >
        <Text style={styles.modalConfirmText}>{buttonText}</Text>
      </TouchableOpacity>
    );
  };

  const getModalMessage = () => {
    if (currentSubscription.collection_method === "charge_automatically") {
      return "Change this subscription from automatic payment to regular invoicing? Future invoices will be sent to the customer, and they will need to pay each one manually.";
    }
    return "Change this subscription from sending invoices to automatic payment? The customer's default payment method will be charged automatically when each payment is due.";
  };

  const validateDaysUntilDue = () => {
    if (currentSubscription.collection_method === "charge_automatically") {
      // Validate when switching to send_invoice
      const days = parseInt(daysUntilDue, 10);
      if (Number.isNaN(days) || days < 0) {
        Alert.alert(
          "Invalid Input",
          "Please enter a valid number of days for the payment due date."
        );
        return false;
      }
    }
    return true;
  };

  const confirmChangeBillingMethod = async () => {
    // Validate input first
    if (!validateDaysUntilDue()) {
      return;
    }

    try {
      setIsChangingBillingMethod(true);
      setShowBillingMethodModal(false);

      // Determine the new collection method (opposite of current one)
      const isCurrentlyAutoCharge =
        currentSubscription.collection_method === "charge_automatically";
      const newCollectionMethod = isCurrentlyAutoCharge
        ? "send_invoice"
        : "charge_automatically";

      // Include daysUntilDue only when switching to send_invoice
      const days = isCurrentlyAutoCharge ? parseInt(daysUntilDue, 10) : null;

      const response = await Database.stripeConnectedAccountUpdateSubscriptionCollectionMethod(
        currentSubscription.id,
        newCollectionMethod,
        days
      );

      if (response.success) {
        // Update the local subscription data
        setCurrentSubscription({
          ...currentSubscription,
          collection_method: newCollectionMethod
        });

        // Prepare user-friendly message
        let methodText = "send invoice";
        if (newCollectionMethod === "charge_automatically") {
          methodText = "charge automatically";
        }

        Alert.alert(
          "Payment Method Updated",
          `The subscription billing method has been changed to ${methodText}.`
        );

        // Call the callback if it exists
        if (route.params?.onSubscriptionUpdated) {
          route.params.onSubscriptionUpdated();
        }
      } else {
        Alert.alert(
          "Error",
          response.message ||
            "Failed to update payment method. Please try again."
        );
      }
    } catch (error) {
      console.error("Error changing payment method:", error);
      Alert.alert(
        "Error",
        "Failed to update payment method. Please try again."
      );
    } finally {
      setIsChangingBillingMethod(false);
    }
  };

  // Format the billing cycle text
  let billingCycle = "";
  if (intervalCount === 1) {
    const intervalMap = {
      month: "monthly",
      year: "yearly",
      week: "weekly",
      day: "daily"
    };
    billingCycle = intervalMap[interval] || `${interval}ly`;
  } else {
    billingCycle = `Every ${intervalCount} ${interval +
      (intervalCount > 1 ? "s" : "")}`;
  }

  const handleCancelSubscription = () => {
    Analytics.logEvent("touch_commerceSubscriptionDetails_cancel");
    setShowCancelModal(true);
  };

  const confirmCancelSubscription = async () => {
    try {
      setIsLoading(true);
      setShowCancelModal(false);

      const response = await Database.stripeConnectedAccountCancelSubscription(
        currentSubscription.id
      );

      if (response.success) {
        // Update the local subscription data
        setCurrentSubscription({
          ...currentSubscription,
          cancel_at_period_end: true
        });

        Alert.alert(
          "Subscription Canceled",
          "The subscription has been canceled and will not renew at the end of the current billing period."
        );

        // Call the callback if it exists
        if (route.params?.onSubscriptionUpdated) {
          route.params.onSubscriptionUpdated();
        }
      } else {
        Alert.alert(
          "Error",
          response.message || "Failed to cancel subscription. Please try again."
        );
      }
    } catch (error) {
      console.error("Error canceling subscription:", error);
      Alert.alert("Error", "Failed to cancel subscription. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Get the child data
  const child = tcChildID ? children[tcChildID] : null;

  const handleChildPress = (childData) => {
    // Navigate to child detail screen
    navigation.push("tcChildDetails", {
      childID: childData.id,
      onUpdateChild: () => {
        // Refresh child data if needed
        fetchChildData();
      }
    });
  };

  // Render child section
  const renderChildContent = () => {
    if (isLoadingChild) {
      return (
        <View style={styles.childCard}>
          <Skeleton size="10" rounded="full" style={{ marginRight: 12 }} />
          <View style={{ flex: 1 }}>
            <Skeleton.Text lines={2} />
          </View>
        </View>
      );
    }

    if (child) {
      return (
        <ChildCard
          child={child}
          classrooms={classrooms}
          onPress={() => handleChildPress(child)}
        />
      );
    }

    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "#2DD881";
      case "past_due":
        return "#F6AD55";
      default:
        return "#718096";
    }
  };

  const getStatusText = (sub) => {
    if (sub.status === "active") {
      return "Active";
    }
    if (
      sub.status === "trialing" &&
      sub.trial_end &&
      sub.trial_end > moment().unix()
    ) {
      return `Starts on ${moment.unix(sub.trial_end).format("MMM D, YYYY")}`;
    }
    return sub.status.charAt(0).toUpperCase() + sub.status.slice(1);
  };

  return (
    <>
      <View style={styles.pageContent}>
        <NavBar navigation={navigation} text="Subscription Details" />
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
        >
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Customer Information</Text>
            <View style={styles.detailRow}>
              <Text style={styles.label}>Name</Text>
              <Text style={styles.value}>
                {currentSubscription.customer?.name || "Customer"}
              </Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.label}>Email</Text>
              <Text style={styles.value}>
                {currentSubscription.customer?.email || "No email"}
              </Text>
            </View>
            {tcChildID && renderChildContent()}
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Subscription Summary</Text>

            <View style={styles.detailRow}>
              <Text style={styles.label}>Status</Text>
              <View style={styles.statusContainer}>
                <View
                  style={[
                    styles.statusDot,
                    {
                      backgroundColor: getStatusColor(
                        currentSubscription.status
                      )
                    }
                  ]}
                />
                <Text
                  style={[
                    styles.value,
                    {
                      color: getStatusColor(currentSubscription.status)
                    }
                  ]}
                >
                  {getStatusText(currentSubscription)}
                </Text>
              </View>
            </View>

            <View style={styles.detailRow}>
              <Text style={styles.label}>Product</Text>
              <Text style={styles.value}>
                {product?.name || "Subscription"}
              </Text>
            </View>

            <View style={styles.detailRow}>
              <Text style={styles.label}>Price</Text>
              <Text style={styles.value}>
                ${totalAmount.toFixed(2)} {billingCycle}
              </Text>
            </View>

            {quantity > 1 && (
              <View style={styles.detailRow}>
                <Text style={styles.label}>Quantity</Text>
                <Text style={styles.value}>{quantity}</Text>
              </View>
            )}

            <View style={styles.detailRow}>
              <Text style={styles.label}>
                {currentSubscription.cancel_at ||
                currentSubscription.cancel_at_period_end
                  ? "Cancelation"
                  : "Next Bill"}
              </Text>
              <Text style={styles.value}>
                {currentSubscription.cancel_at ||
                currentSubscription.cancel_at_period_end
                  ? "Cancels on "
                  : ""}
                {moment
                  .unix(currentSubscription.current_period_end)
                  .format("MMM D, YYYY")}
              </Text>
            </View>

            <View style={styles.detailRow}>
              <Text style={styles.label}>Created</Text>
              <Text style={styles.value}>
                {moment.unix(currentSubscription.created).format("MMM D, YYYY")}
              </Text>
            </View>

            <View style={styles.detailRow}>
              <Text style={styles.label}>Billing Method</Text>
              <TouchableOpacity
                style={{ flexDirection: "row", alignItems: "center" }}
                onPress={handleChangeBillingMethod}
                disabled={!hasDefaultPaymentMethod || isChangingBillingMethod}
              >
                <Text
                  style={[
                    styles.value,
                    {
                      color: hasDefaultPaymentMethod
                        ? Rex.getConfig()?.colors?.button
                        : "#2D3748"
                    }
                  ]}
                >
                  {currentSubscription.collection_method ===
                  "charge_automatically"
                    ? "Charge automatically"
                    : "Send invoice"}
                </Text>
                {hasDefaultPaymentMethod ||
                currentSubscription.collection_method ===
                  "charge_automatically" ? (
                  <Icon
                    icon="d2b21926-3f36-4f31-a8d4-20e23da39357" // edit
                    color={Rex.getConfig()?.colors?.button}
                    size={14}
                    style={{ marginLeft: 4 }}
                  />
                ) : null}
              </TouchableOpacity>
            </View>

            {!!currentSubscription.stripeURL && (
              <View style={styles.detailRow}>
                <Text style={styles.label}>Stripe Link</Text>
                <TouchableOpacity
                  style={{ flexDirection: "row", alignItems: "center" }}
                  onPress={() => Util.openURL(currentSubscription.stripeURL)}
                >
                  <Text
                    style={[
                      styles.value,
                      { color: Rex.getConfig()?.colors?.button }
                    ]}
                  >
                    View in Stripe
                  </Text>
                  <Icon
                    icon="d24166f4-d993-4d3a-8692-ae839b70b6af"
                    color={Rex.getConfig()?.colors?.button}
                    size={14}
                    style={{ marginLeft: 4 }}
                  />
                </TouchableOpacity>
              </View>
            )}

            {/* Cancellation notice */}
            {(currentSubscription.cancel_at ||
              currentSubscription.cancel_at_period_end) && (
              <View style={styles.cancelNotice}>
                <MaterialIcons name="info" size={20} color="#E53E3E" />
                <Text style={styles.cancelText}>
                  This subscription will{" "}
                  {currentSubscription.cancel_at
                    ? `end on ${moment
                        .unix(currentSubscription.cancel_at)
                        .format("MMM D, YYYY")}`
                    : `not renew after ${moment
                        .unix(currentSubscription.current_period_end)
                        .format("MMM D, YYYY")}`}
                </Text>
              </View>
            )}
          </View>

          <View style={styles.actions}>
            {currentSubscription.status === "active" &&
              !currentSubscription.cancel_at_period_end && (
                <TouchableOpacity
                  style={[styles.actionButton, styles.cancelButton]}
                  onPress={handleCancelSubscription}
                  disabled={isLoading}
                >
                  <MaterialIcons name="cancel" size={20} color="#DC2626" />
                  <Text
                    style={[styles.actionButtonText, styles.cancelButtonText]}
                  >
                    {isLoading ? "Processing..." : "Cancel Subscription"}
                  </Text>
                </TouchableOpacity>
              )}
          </View>
        </ScrollView>
      </View>

      {/* Cancel Confirmation Modal */}
      <Modal
        visible={showCancelModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCancelModal(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowCancelModal(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <Text style={styles.modalTitle}>Cancel Subscription</Text>
                <Text style={styles.modalMessage}>
                  Are you sure you want to cancel this subscription? The
                  customer will still have access until the end of the current
                  billing period (
                  {moment
                    .unix(currentSubscription.current_period_end)
                    .format("MMM D, YYYY")}
                  ).
                </Text>
                <View style={styles.modalButtons}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.modalCancelButton]}
                    onPress={() => setShowCancelModal(false)}
                  >
                    <Text style={styles.modalCancelText}>No, Keep Active</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.modalConfirmButton]}
                    onPress={confirmCancelSubscription}
                  >
                    <Text style={styles.modalConfirmText}>
                      Yes, Cancel Subscription
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Billing Method Change Confirmation Modal */}
      <Modal
        visible={showBillingMethodModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowBillingMethodModal(false)}
      >
        <TouchableWithoutFeedback
          onPress={() => setShowBillingMethodModal(false)}
        >
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <Text style={styles.modalTitle}>Change Billing Method</Text>
                <Text style={styles.modalMessage}>{getModalMessage()}</Text>

                {/* Payment Due Input - Show only when switching to send_invoice */}
                {currentSubscription.collection_method ===
                  "charge_automatically" && (
                  <View style={styles.paymentDueContainer}>
                    <Text style={styles.paymentDueLabel}>Payment due</Text>
                    <View style={styles.paymentDueInputContainer}>
                      <TextInput
                        style={styles.paymentDueInput}
                        value={daysUntilDue}
                        onChangeText={setDaysUntilDue}
                        keyboardType="number-pad"
                        maxLength={3}
                      />
                      <Text style={styles.paymentDueText}>
                        days after invoice is sent
                      </Text>
                    </View>
                  </View>
                )}

                <View style={styles.modalButtons}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.modalCancelButton]}
                    onPress={() => setShowBillingMethodModal(false)}
                  >
                    <Text style={styles.modalCancelText}>Cancel</Text>
                  </TouchableOpacity>
                  {renderBillingMethodButton()}
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  pageContent: {
    flex: 1,
    alignItems: "center",
    backgroundColor: "#F7FAFC"
  },
  scrollView: {
    flex: 1,
    padding: 16,
    width
  },
  scrollViewContent: {
    paddingBottom: 40
  },
  section: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2D3748",
    marginBottom: 16
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF2F7"
  },
  label: {
    fontSize: 16,
    color: "#718096"
  },
  value: {
    fontSize: 16,
    color: "#2D3748",
    fontWeight: "500"
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center"
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6
  },
  cancelNotice: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFF5F5",
    padding: 12,
    borderRadius: 6,
    marginTop: 16
  },
  cancelText: {
    marginLeft: 10,
    color: "#E53E3E",
    fontSize: 14
  },
  actions: {
    marginTop: 8,
    marginBottom: 20
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2
  },
  actionButtonText: {
    marginLeft: 12,
    fontSize: 16,
    color: "#4A5568",
    fontWeight: "500"
  },
  cancelButton: {
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#FECACA"
  },
  cancelButtonText: {
    color: "#DC2626"
  },
  childCard: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: 12,
    padding: 20,
    width: "100%",
    maxWidth: 500,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2D3748",
    marginBottom: 10
  },
  modalMessage: {
    fontSize: 16,
    color: "#4A5568",
    marginBottom: 20
  },
  modalButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 10
  },
  modalButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    marginHorizontal: 5
  },
  modalCancelButton: {
    backgroundColor: "#EDF2F7",
    borderWidth: 1,
    borderColor: "#E2E8F0"
  },
  modalConfirmButton: {
    backgroundColor: "#DC2626"
  },
  modalCancelText: {
    color: "#4A5568",
    fontWeight: "500",
    fontSize: 16
  },
  modalConfirmText: {
    color: "white",
    fontWeight: "500",
    fontSize: 16
  },
  paymentDueContainer: {
    marginTop: 16,
    marginBottom: 8
  },
  paymentDueLabel: {
    fontSize: 16,
    fontWeight: "500",
    color: "#2D3748",
    marginBottom: 8
  },
  paymentDueInputContainer: {
    flexDirection: "row",
    alignItems: "center"
  },
  paymentDueInput: {
    borderWidth: 1,
    borderColor: "#CBD5E0",
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
    width: 60,
    marginRight: 8
  },
  paymentDueText: {
    fontSize: 16,
    color: "#4A5568"
  }
});
